interface DIDConfig {
  apiKey: string;
  baseUrl: string;
}

interface CreateTalkRequest {
  script: {
    type: 'text';
    input: string;
    provider?: {
      type: 'microsoft';
      voice_id: string;
      voice_config?: {
        style?: string;
        rate?: string;
        pitch?: string;
      };
    };
  };
  source_url?: string;
  presenter_id?: string;
  driver_url?: string;
  config?: {
    fluent?: boolean;
    pad_audio?: number;
    stitch?: boolean;
    result_format?: 'mp4' | 'gif' | 'mov';
  };
}

interface TalkResponse {
  id: string;
  status: 'created' | 'started' | 'done' | 'error';
  result_url?: string;
  error?: {
    kind: string;
    description: string;
  };
}

class DIDService {
  private config: DIDConfig;

  constructor() {
    this.config = {
      apiKey: import.meta.env.VITE_DID_API_KEY || '',
      baseUrl: 'https://api.d-id.com'
    };
  }

  async createTalk(text: string, presenterUrl?: string): Promise<TalkResponse> {
    if (!this.config.apiKey) {
      throw new Error('D-ID API key not configured. Please set VITE_DID_API_KEY environment variable.');
    }

    const requestBody: CreateTalkRequest = {
      script: {
        type: 'text',
        input: text,
        provider: {
          type: 'microsoft',
          voice_id: 'en-GB-RyanNeural', // British English voice for DVSA context
          voice_config: {
            style: 'friendly',
            rate: '0.9', // Slightly slower for training content
            pitch: 'medium'
          }
        }
      },
      source_url: presenterUrl || 'https://create-images-results.d-id.com/DefaultPresenters/Noelle_f/image.jpeg',
      config: {
        fluent: true,
        pad_audio: 0.5,
        stitch: true,
        result_format: 'mp4'
      }
    };

    try {
      const response = await fetch(`${this.config.baseUrl}/talks`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${this.config.apiKey}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        let errorMessage = '';
        
        // Handle specific HTTP status codes
        switch (response.status) {
          case 401:
            errorMessage = 'Invalid D-ID API key. Please check your VITE_DID_API_KEY in the .env file.';
            break;
          case 402:
            errorMessage = 'Insufficient D-ID credits. Please add credits to your D-ID account at https://studio.d-id.com/account-settings';
            break;
          case 403:
            errorMessage = 'D-ID API access forbidden. Please verify your account permissions.';
            break;
          case 429:
            errorMessage = 'D-ID API rate limit exceeded. Please wait a moment and try again.';
            break;
          case 500:
          case 502:
          case 503:
            errorMessage = 'D-ID service temporarily unavailable. Please try again later.';
            break;
          default:
            try {
              const errorData = await response.json();
              errorMessage = `D-ID API error: ${response.status} - ${errorData.message || errorData.description || response.statusText}`;
            } catch {
              errorMessage = `D-ID API error: ${response.status} - ${response.statusText}`;
            }
        }
        
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to D-ID API. Please check your internet connection.');
      }
      
      console.error('Error creating D-ID talk:', error);
      throw error;
    }
  }

  async getTalkStatus(talkId: string): Promise<TalkResponse> {
    if (!this.config.apiKey) {
      throw new Error('D-ID API key not configured');
    }

    try {
      const response = await fetch(`${this.config.baseUrl}/talks/${talkId}`, {
        headers: {
          'Authorization': `Basic ${this.config.apiKey}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        let errorMessage = '';
        
        switch (response.status) {
          case 401:
            errorMessage = 'Invalid D-ID API key';
            break;
          case 404:
            errorMessage = 'Talk not found';
            break;
          default:
            errorMessage = `Failed to get talk status: ${response.status}`;
        }
        
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to D-ID API');
      }
      
      console.error('Error getting talk status:', error);
      throw error;
    }
  }

  async waitForCompletion(talkId: string, maxWaitTime = 60000): Promise<TalkResponse> {
    const startTime = Date.now();
    const pollInterval = 2000; // Poll every 2 seconds

    while (Date.now() - startTime < maxWaitTime) {
      const status = await this.getTalkStatus(talkId);
      
      if (status.status === 'done') {
        return status;
      }
      
      if (status.status === 'error') {
        throw new Error(`Talk generation failed: ${status.error?.description || 'Unknown error'}`);
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }

    throw new Error('Talk generation timed out');
  }

  // Get available presenters
  async getPresenters(): Promise<any[]> {
    if (!this.config.apiKey) {
      throw new Error('D-ID API key not configured');
    }

    try {
      const response = await fetch(`${this.config.baseUrl}/images`, {
        headers: {
          'Authorization': `Basic ${this.config.apiKey}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to get presenters: ${response.status}`);
      }

      const data = await response.json();
      return data.images || [];
    } catch (error) {
      console.error('Error getting presenters:', error);
      return [];
    }
  }
}

export const didService = new DIDService();
export type { TalkResponse, CreateTalkRequest };